/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminUsers\AdminUserBlocked.js
 * ВЕРСИЯ: 1.0.0
 * ДАТА СОЗДАНИЯ: 15 ноября 2025
 * ВРЕМЯ: 15:40
 * 
 * ОПИСАНИЕ: Компонент для управления заблокированными пользователями
 * - Показывает список заблокированных пользователей
 * - Причины блокировки
 * - Разблокировка пользователей
 * - История действий
 */

import React, { useState, useEffect } from 'react';
import './AdminUsers.css';

const AdminUserBlocked = () => {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterReason, setFilterReason] = useState('all');
  const [unblockConfirm, setUnblockConfirm] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://qr-money.onrender.com/api/admin/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Ошибка загрузки пользователей');
      
      const data = await response.json();
      const userList = data.data || data.users || data || [];
      const users = Array.isArray(userList) ? userList : [];
      
      setAllUsers(users);
      // Фильтруем только заблокированных
      const blocked = users.filter(u => u.status === 'blocked');
      setBlockedUsers(blocked);
    } catch (err) {
      console.error('❌ Ошибка:', err);
      setError(err.message);
      setBlockedUsers(getMockBlockedUsers());
    } finally {
      setLoading(false);
    }
  };

  const getMockBlockedUsers = () => [
    {
      id: 4,
      email: 'blocked-user@test.com',
      username: 'BlockedUser',
      role: 'user',
      status: 'blocked',
      created_at: '2025-10-01T10:00:00Z',
      blocked_at: '2025-11-10T15:30:00Z',
      block_reason: 'suspicious_activity',
      block_details: 'Обнаружена подозрительная активность: множественные неудачные попытки входа',
      blocked_by: 'admin@test.com'
    },
    {
      id: 5,
      email: 'fraud-user@test.com',
      username: 'FraudUser',
      role: 'user',
      status: 'blocked',
      created_at: '2025-09-15T10:00:00Z',
      blocked_at: '2025-11-05T12:00:00Z',
      block_reason: 'fraud_detected',
      block_details: 'Обнаружено мошенничество: создание подделанных QR кодов',
      blocked_by: 'admin@test.com'
    },
    {
      id: 6,
      email: 'spam-user@test.com',
      username: 'SpamUser',
      role: 'business',
      status: 'blocked',
      created_at: '2025-08-20T10:00:00Z',
      blocked_at: '2025-11-08T09:15:00Z',
      block_reason: 'spam',
      block_details: 'Нарушение условий: рассылка спама и неправомерное использование платформы',
      blocked_by: 'admin@test.com'
    }
  ];

  const unblockUser = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://qr-money.onrender.com/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'active' })
      });

      if (!response.ok) throw new Error('Ошибка разблокировки');
      
      alert('✅ Пользователь разблокирован!');
      setUnblockConfirm(null);
      fetchUsers();
    } catch (err) {
      alert(`❌ Ошибка: ${err.message}`);
    }
  };

  const blockReasonDescriptions = {
    suspicious_activity: { icon: '⚠️', label: 'Подозрительная активность', color: '#ff9800' },
    fraud_detected: { icon: '🚨', label: 'Обнаружено мошенничество', color: '#f44336' },
    spam: { icon: '📧', label: 'Спам', color: '#ff6f00' },
    abuse: { icon: '⚔️', label: 'Оскорбительное поведение', color: '#d32f2f' },
    violation: { icon: '⛔', label: 'Нарушение условий', color: '#c62828' }
  };

  const filteredUsers = filterReason === 'all' 
    ? blockedUsers 
    : blockedUsers.filter(u => u.block_reason === filterReason);

  if (loading) return <div className="admin-container">⏳ Загрузка...</div>;

  return (
    <div className="admin-container">
      <h1>🚫 Заблокированные пользователи</h1>

      {error && <div className="error-banner">⚠️ {error}</div>}

      {blockedUsers.length === 0 ? (
        <div className="empty-state">
          <h2>✅ Нет заблокированных пользователей</h2>
          <p>Все пользователи активны и соблюдают правила платформы!</p>
        </div>
      ) : (
        <>
          {/* СТАТИСТИКА */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{blockedUsers.length}</div>
              <div className="stat-label">Всего заблокировано</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{blockedUsers.filter(u => u.block_reason === 'fraud_detected').length}</div>
              <div className="stat-label">Мошенничество</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{blockedUsers.filter(u => u.block_reason === 'suspicious_activity').length}</div>
              <div className="stat-label">Подозрительная активность</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{blockedUsers.filter(u => u.block_reason === 'spam').length}</div>
              <div className="stat-label">Спам</div>
            </div>
          </div>

          {/* ФИЛЬТР */}
          <div className="filter-section">
            <label>Фильтр по причине:</label>
            <select 
              value={filterReason}
              onChange={(e) => setFilterReason(e.target.value)}
              className="filter-select"
            >
              <option value="all">Все причины</option>
              <option value="suspicious_activity">⚠️ Подозрительная активность</option>
              <option value="fraud_detected">🚨 Мошенничество</option>
              <option value="spam">📧 Спам</option>
              <option value="abuse">⚔️ Оскорбительное поведение</option>
              <option value="violation">⛔ Нарушение условий</option>
            </select>
            <div className="filter-count">
              Найдено: <strong>{filteredUsers.length}</strong>
            </div>
          </div>

          {/* ТАБЛИЦА ЗАБЛОКИРОВАННЫХ */}
          <div className="blocked-list">
            {filteredUsers.map(user => {
              const reasonInfo = blockReasonDescriptions[user.block_reason] || 
                { icon: '❓', label: user.block_reason, color: '#999' };
              
              return (
                <div key={user.id} className="blocked-card">
                  <div className="card-header">
                    <div className="user-info">
                      <h3>{user.username || user.email}</h3>
                      <p className="email">{user.email}</p>
                    </div>
                    <div className="block-reason">
                      <span 
                        className="reason-badge"
                        style={{ backgroundColor: reasonInfo.color }}
                      >
                        {reasonInfo.icon} {reasonInfo.label}
                      </span>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="info-grid">
                      <div className="info-item">
                        <strong>Роль:</strong>
                        <span className={`role-badge role-${user.role}`}>
                          {user.role === 'user' ? '🔵 Пользователь' : '🟢 Бизнес'}
                        </span>
                      </div>
                      <div className="info-item">
                        <strong>Регистрация:</strong>
                        <span>{new Date(user.created_at).toLocaleDateString('ru-RU')}</span>
                      </div>
                      <div className="info-item">
                        <strong>Блокировка:</strong>
                        <span>{new Date(user.blocked_at).toLocaleDateString('ru-RU')}</span>
                      </div>
                      <div className="info-item">
                        <strong>Заблокирован:</strong>
                        <span>{user.blocked_by}</span>
                      </div>
                    </div>

                    <div className="details-section">
                      <strong>📝 Причина блокировки:</strong>
                      <p className="details-text">{user.block_details}</p>
                    </div>
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => setUnblockConfirm(user.id)}
                    >
                      🔓 Разблокировать
                    </button>
                    <button className="btn btn-secondary">
                      📋 Посмотреть историю
                    </button>
                  </div>

                  {/* ПОДТВЕРЖДЕНИЕ РАЗБЛОКИРОВКИ */}
                  {unblockConfirm === user.id && (
                    <div className="confirmation-box">
                      <p>⚠️ Вы уверены что хотите разблокировать пользователя {user.email}?</p>
                      <div className="confirmation-buttons">
                        <button
                          className="btn btn-primary"
                          onClick={() => unblockUser(user.id)}
                        >
                          ✅ Да, разблокировать
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setUnblockConfirm(null)}
                        >
                          ❌ Отмена
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <style jsx>{`
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
          margin: 20px 0;
        }

        .stat-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }

        .stat-number {
          font-size: 32px;
          font-weight: bold;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 12px;
          opacity: 0.9;
        }

        .filter-section {
          background: #f5f5f5;
          padding: 15px;
          border-radius: 8px;
          margin: 20px 0;
          display: flex;
          gap: 15px;
          align-items: center;
        }

        .filter-select {
          flex: 1;
          max-width: 300px;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .filter-count {
          margin-left: auto;
          font-size: 14px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-state h2 {
          color: #28a745;
          font-size: 28px;
        }

        .blocked-list {
          display: grid;
          gap: 20px;
        }

        .blocked-card {
          border: 1px solid #ddd;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background: #f9f9f9;
          border-bottom: 1px solid #eee;
        }

        .user-info h3 {
          margin: 0;
          font-size: 16px;
        }

        .email {
          color: #666;
          font-size: 13px;
          margin: 5px 0 0 0;
        }

        .reason-badge {
          padding: 6px 12px;
          border-radius: 20px;
          color: white;
          font-size: 13px;
          font-weight: bold;
        }

        .card-body {
          padding: 20px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .info-item strong {
          font-size: 12px;
          color: #666;
          text-transform: uppercase;
        }

        .info-item span {
          font-size: 14px;
        }

        .details-section {
          background: #fff3cd;
          border-left: 4px solid #ffc107;
          padding: 15px;
          border-radius: 4px;
        }

        .details-text {
          margin: 8px 0 0 0;
          color: #333;
          line-height: 1.6;
        }

        .card-actions {
          display: flex;
          gap: 10px;
          padding: 15px;
          border-top: 1px solid #eee;
          background: #f9f9f9;
        }

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          flex: 1;
        }

        .btn-primary {
          background: #28a745;
          color: white;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .confirmation-box {
          background: #fff3cd;
          padding: 15px;
          border-top: 1px solid #ffc107;
          margin-top: 0;
        }

        .confirmation-buttons {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }

        .role-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }

        .role-user {
          background: #d4e6ff;
          color: #0056b3;
        }

        .role-business {
          background: #d4edda;
          color: #155724;
        }
      `}</style>
    </div>
  );
};

export default AdminUserBlocked;
